import React, { useEffect, useMemo, useRef, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { cn } from "../../../lib/utils";
import { ExpenseHeads } from "../@types";
import { useGetAllExpenseHeadsMutation } from "../financeApiSlice";

interface ExpenseHeadComboInputProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  label?: string;
  error?: string;
  type?: string;
}

const ExpenseHeadComboInput: React.FC<ExpenseHeadComboInputProps> = ({
  value = "",
  onChange,
  className,
  disabled,
  placeholder = "Enter description",
  name = "expense",
  id = "expense",
  showLabel = true,
  error,
  label = "Description",
  type = "text",
}) => {
  const [
    getExpenseHead,
    { data: fines, isLoading, isError, error: errorData },
  ] = useGetAllExpenseHeadsMutation();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const expenseHeads = useMemo(() => {
    return Array.isArray(fines?.data.data) ? fines?.data.data : [];
  }, [fines?.data.data]);

  const filteredOptions = useMemo(() => {
    if (!Array.isArray(expenseHeads)) return [];
    if (!value) return expenseHeads;

    return expenseHeads.filter((head: ExpenseHeads) =>
      head.name.toLowerCase().includes(value.toLowerCase())
    );
  }, [value, expenseHeads]);

  useEffect(() => {
    getExpenseHead({ pageNumber: 0, pageSize: 100 });
  }, [getExpenseHead]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    setShowDropdown(true);
  };

  const handleSelectOption = (optionValue: string) => {
    // Create a synthetic event to match the expected onChange signature
    const syntheticEvent = {
      target: {
        name,
        value: optionValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (!disabled && Array.isArray(expenseHeads) && expenseHeads.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div className={cn("w-full relative")}>
      {showLabel && (
        <label htmlFor={id} className="block mb-1 font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "bg-[#F6F6F6] border border-[#F6F6F6] h-[48px] w-full rounded-md px-3 pr-10 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary",
            className
          )}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled || isLoading}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
          aria-label="Toggle dropdown"
        >
          <svg
            className={cn(
              "w-4 h-4 text-gray-500 transition-transform",
              showDropdown && "rotate-180"
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {showDropdown && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-70 overflow-auto"
          >
            {isLoading && (
              <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
            )}
            {isError && (
              <div className="px-3 py-2 text-sm text-red-500">
                {getErrorDetails(errorData)?.message || "Error loading options"}
              </div>
            )}
            {!isLoading && !isError && filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No options found
              </div>
            )}
            {!isLoading &&
              !isError &&
              filteredOptions.map((head: ExpenseHeads) => (
                <button
                  key={head.id || head.name}
                  type="button"
                  onClick={() => handleSelectOption(head.name)}
                  className={cn(
                    "w-full text-left px-3 py-3 text-sm hover:bg-gray-100 transition-colors",
                    value === head.name && "bg-gray-50 font-medium"
                  )}
                >
                  {head.name}
                </button>
              ))}
          </div>
        )}
      </div>

      {isError && !showDropdown && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading options"}
        </p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default ExpenseHeadComboInput;
